"use client";

import React, { FC } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/* eslint-disable */

type BaseCarouselProps = {
  data: any[];
  itemRender: (item: any) => React.ReactNode;
  id: string;
};

export const BaseCarousel: FC<BaseCarouselProps> = ({
  data,
  itemRender,
  id,
}) => {
  const theme = useTheme();
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);

  // If there's only one item, render it directly without Swiper
  if (data.length === 1) {
    return (
      <Box sx={{ paddingBottom: "3rem", width: "100%" }}>
        <Box mb={1} sx={{ height: "100%", width: "100%" }}>
          {itemRender(data[0])}
        </Box>
      </Box>
    );
  }

  // If no data, return null
  if (data.length === 0) {
    return null;
  }

  return (
    <Swiper
      modules={[Navigation, Pagination]}
      navigation={{
        prevEl: ".custom-prev-button",
        nextEl: ".custom-next-button",
      }}
      spaceBetween={30}
      slidesPerView={1}
      breakpoints={{
        [theme.breakpoints.values.md]: {
          slidesPerView: Math.min(2, data.length),
        },
      }}
      style={{ paddingBottom: "3rem" }}
      onSwiper={(swiper: SwiperCore) => {
        // Set initial states
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);

        swiper.on("slideChange", () => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        });

        swiper.on("resize", () => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        });

        // Force update states after initialization
        setTimeout(() => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }, 10);
      }}
    >
      {data.map((item) => (
        <SwiperSlide key={item[id]} style={{ height: "100%", display: "flex" }}>
          <Box mb={1} sx={{ height: "100%", width: "100%" }}>
            {itemRender(item)}
          </Box>
        </SwiperSlide>
      ))}

      {/* Custom arrows - only show if more than one item */}
      {data.length > 1 && (
        <>
          <IconButton
            className="custom-prev-button"
            color="primary"
            sx={{
              position: "absolute",
              bottom: 0,
              right: "4rem",
              zIndex: 10,
            }}
            disabled={isBeginning}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <IconButton
            className="custom-next-button"
            color="primary"
            sx={{
              position: "absolute",
              bottom: 0,
              right: "1rem",
              zIndex: 10,
            }}
            disabled={isEnd}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </>
      )}
    </Swiper>
  );
};
